import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface LargeIconSelectButtonProps {
    id: string;
    label: string;
    /**
     * Accepts either a FontAwesome `IconDefinition` (rendered via `FontAwesomeIcon`)
     * or a string URL to an SVG asset (rendered as an `<img>`). Use a string URL
     * when the icon needs to be multi-color, which FontAwesome does not support.
     */
    icon: IconDefinition | string;
    /** Alt text used when `icon` is an SVG URL. Defaults to an empty string (decorative). */
    iconAlt?: string;
    iconSize?: SizeProp;
    iconColor?: string;
    isSelected: boolean;
    onSelect: (id: string, isSelected: boolean) => void;
    testId?: string;
}

function LargeIconSelectButton(props: LargeIconSelectButtonProps) {
    const {
        id,
        label,
        icon,
        iconAlt,
        iconSize = '4x',
        iconColor,
        isSelected,
        onSelect,
        testId,
    } = props;

    const { spacing, palette } = useTheme();

    const handleClick = () => {
        onSelect(id, !isSelected);
    };

    const isSvg = typeof icon === 'string';

    return (
        <ButtonBase
            onClick={handleClick}
            data-testid={testId ?? `observation-select-${id}`}
            aria-pressed={isSelected}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                width: '100%',
                minHeight: spacing(15),
                borderRadius: 3,
                backgroundColor: palette.common.white,
                boxShadow: isSelected
                    ? `inset 0 0 0 3px ${palette.primary.main}`
                    : `inset 0 0 0 1px ${palette.grey[200]}`,
                transition: 'all 0.15s ease-in-out',
                p: 1.5,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: spacing(6),
                    minHeight: spacing(6),
                }}
            >
                {isSvg ? (
                    <Box
                        component="img"
                        src={icon}
                        alt={iconAlt ?? ''}
                        sx={{
                            height: spacing(8),
                            width: 'auto',
                            maxWidth: spacing(10),
                            objectFit: 'contain',
                        }}
                    />
                ) : (
                    <FontAwesomeIcon
                        icon={icon}
                        size={iconSize}
                        color={iconColor ?? palette.grey[900]}
                    />
                )}
            </Box>
            <Typography
                variant="body1"
                sx={{
                    fontWeight: 600,
                    color: palette.grey[900],
                    textAlign: 'center',
                    lineHeight: 1.2,
                    textWrap: 'balance',
                    wordBreak: 'break-word',
                }}
            >
                {label}
            </Typography>
        </ButtonBase>
    );
}

export default LargeIconSelectButton;
